/**
 * Input Component - Campo de formulario reutilizable
 *
 * QUÉ HACE:
 * - Proporciona inputs estilizados con label, error, icono y helper text
 * - Soporta todos los tipos HTML nativos de input
 * - Compatible con react-hook-form via register()
 *
 * QUÉ EXPORTA: Input (named), TextArea (named), FormField (named)
 *
 * QUÉ NO DEBE HACER:
 * - NO debe validar datos internamente (eso es responsabilidad de zod/react-hook-form)
 * - NO debe contener lógica de negocio
 *
 * REGLAS:
 * - TODO campo de formulario DEBE usar estos componentes
 * - Los estilos deben ser consistentes con el design system
 *
 * Uso:
 *   <Input label="Email" type="email" error={errors.email?.message} {...register("email")} />
 *   <TextArea label="Notas" rows={4} {...register("notes")} />
 *   <FormField label="Nombre" error={errors.name?.message}>
 *     <CustomInput />
 *   </FormField>
 */

import {
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
  forwardRef,
} from "react";

// ─── FormField wrapper (label + error para inputs custom) ────────────────────

interface FormFieldProps {
  /** Texto del label */
  label?: string;
  /** Mensaje de error */
  error?: string;
  /** Texto de ayuda debajo del campo */
  helperText?: string;
  /** Marca como requerido visualmente */
  required?: boolean;
  /** Contenido del campo */
  children: ReactNode;
  /** Clase CSS adicional */
  className?: string;
}

export const FormField = ({
  label,
  error,
  helperText,
  required = false,
  children,
  className = "",
}: FormFieldProps) => {
  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>
      )}
      {helperText && !error && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {helperText}
        </p>
      )}
    </div>
  );
};

// ─── Input ───────────────────────────────────────────────────────────────────

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      icon,
      rightIcon,
      className = "",
      required,
      ...props
    },
    ref,
  ) => {
    const baseStyles =
      "w-full px-4 py-2.5 text-sm border rounded-xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:text-gray-500 disabled:cursor-not-allowed bg-white dark:bg-slate-800 dark:text-gray-100";
    const stateStyles = error
      ? "border-red-300 dark:border-red-700 focus:ring-red-500/20 focus:border-red-500"
      : "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500";

    return (
      <FormField
        label={label}
        error={error}
        helperText={helperText}
        required={required}
      >
        <div className="relative">
          {icon && <div className="input-icon-container left-3">{icon}</div>}
          <input
            ref={ref}
            className={`
              ${baseStyles}
              ${stateStyles}
              ${icon ? "pl-10" : ""}
              ${rightIcon ? "pr-10" : ""}
              ${className}
            `}
            {...props}
          />
          {rightIcon && (
            <div className="input-icon-container right-3">{rightIcon}</div>
          )}
        </div>
      </FormField>
    );
  },
);

Input.displayName = "Input";

// ─── TextArea ────────────────────────────────────────────────────────────────

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, error, helperText, className = "", required, ...props }, ref) => {
    const baseStyles =
      "w-full px-4 py-2.5 text-sm border rounded-xl transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-slate-800 disabled:text-gray-500 resize-none bg-white dark:bg-slate-800 dark:text-gray-100";
    const stateStyles = error
      ? "border-red-300 dark:border-red-700 focus:ring-red-500/20 focus:border-red-500"
      : "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500";

    return (
      <FormField
        label={label}
        error={error}
        helperText={helperText}
        required={required}
      >
        <textarea
          ref={ref}
          className={`${baseStyles} ${stateStyles} ${className}`}
          {...props}
        />
      </FormField>
    );
  },
);

TextArea.displayName = "TextArea";
